import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
  Stack,
  Paper,
  Grid,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Fade,
  Zoom,
  Tooltip,
  Container
} from '@mui/material'
import localization from '../utils/localization'
import ContentUtils from '../utils/content'
import ContentItem from '../components/ContentItem'
import FormInput from '../components/FormInput'
import ColorUtils from '../utils/colors'
import UserUtils from '../utils/user'
import AesUtils from '../utils/aes'
import copy from 'copy-to-clipboard'
import toast from 'react-hot-toast'
import ServerUtils from '../utils/server'
import PasswordForm from '../components/PasswordForm'
import TextForm from '../components/TextForm'
import TotpForm from '../components/TotpForm'
import BlockchainForm from '../components/BlockchainForm'
import { QRCodeSVG } from 'qrcode.react'

const ValidityPeriods = [{
  value: 10 * 60,
  desc: '10 ' + localization.strings.minute,
}, {
  value: 30 * 60,
  desc: '30 ' + localization.strings.minute,
}, {
  value: 60 * 60,
  desc: '1 ' + localization.strings.hour,
}, {
  value: 3 * 60 * 60,
  desc: '3 ' + localization.strings.hour,
}, {
  value: 6 * 60 * 60,
  desc: '6 ' + localization.strings.hour,
}, {
  value: 12 * 60 * 60,
  desc: '12 ' + localization.strings.hour,
}, {
  value: 24 * 60 * 60,
  desc: '1 ' + localization.strings.day,
}]

function MainMessage() {
  const [contentList, setContentList] = useState([])
  const [dialogTitle, setDialogTitle] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [step, setStep] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [mode, setMode] = useState('')
  const [contentType, setContentType] = useState(ContentUtils.getPasswordType())
  const [contentInfo, setContentInfo] = useState({})
  const [validityPeriod, setValidityPeriod] = useState(600)
  const [tempPassword, setTempPassword] = useState('')
  const [tempPasswordErr, setTempPasswordErr] = useState('')
  const [hintWord, setHintWord] = useState('')
  const [submit, setSubmit] = useState(false)
  const [link, setLink] = useState(false)
  const passwordFormRef = useRef();
  const textFormRef = useRef();
  const totpFormRef = useRef();
  const blockchainFormRef = useRef();

  useEffect(() => {
    if (dialogOpen) {
      setContentType(ContentUtils.getPasswordType())
      setStep(0)
    }
  }, [dialogOpen])

  useEffect(() => {
    ContentUtils.setContentListener(async (rawContentList) => {
      for (var i = 0; i < rawContentList.length; i++) {
        if (rawContentList[i].type === ContentUtils.getBlockChainType()) {
          rawContentList[i].detail = JSON.parse(rawContentList[i].content)
        }
      }
      setContentList(rawContentList)
    })
  }, [])

  function doPack() {
    if (tempPassword === '') {
      setTempPasswordErr(localization.strings.required)
      return
    }
    if (submit) return
    setSubmit(true)
    setStep(2)
    let userInfo = UserUtils.getUserInfo()
    ServerUtils.sendMessage({
      encrypted_data: AesUtils.encryptData(userInfo.account, tempPassword, JSON.stringify(contentInfo)),
      expire_time: validityPeriod,
      sign: AesUtils.sha256(tempPassword),
      color: contentInfo.color,
      hint_word: hintWord,
    }).then(async (resp) => {
      setSubmit(false)
      if (!resp.ok) {
        setStep(1)
        try {
          let respJson = await resp.json()
          if (respJson.errno !== 0) {
            toast.error(ServerUtils.handleError(respJson))
            return
          }
        } catch (e) { }
        toast.error(localization.strings.networkErr)
        return
      }
      let respJson = await resp.json()
      if (respJson.errno !== 0) {
        setStep(1)
        toast.error(ServerUtils.handleError(respJson))
        return
      }
      setLink(`${window.origin}/message.html?key=${respJson.data.content_key}`)
      setStep(3)
      setTempPassword('')
      setHintWord('')
    })
  }

  const steps = [
    mode === 'sendExistPassword' ? localization.strings.msgStepSelect : localization.strings.msgStepCreate,
    localization.strings.msgStepSecurity,
    localization.strings.msgStepLink
  ];

  return (
    <Box sx={{
      height: '100%',
      overflowY: 'auto',
      backgroundColor: '#F8FAFC',
    }}>
      <Box sx={{
        marginTop: '5%',
        p: { xs: 2, sm: 4 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <Container maxWidth="md" sx={{ my: 'auto', width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1E293B', fontFamily: "'Poppins', sans-serif", mb: 1.5 }}>
              {localization.strings.encryptedMessage}
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', maxWidth: '500px', mx: 'auto' }}>
              {localization.strings.mainMessageDesc}
            </Typography>
          </Box>

          <Grid container spacing={4} sx={{ maxWidth: '900px', justifyContent: 'center', mx: 'auto' }}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                onClick={() => {
                  setStep(0)
                  setContentInfo({})
                  setDialogOpen(true)
                  setMode('sendExistPassword')
                  setDialogTitle(localization.strings.sendExistPassword)
                }}
                sx={{
                  p: 5,
                  height: '100%',
                  borderRadius: '32px',
                  border: '1px solid rgba(0,0,0,0.04)',
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  backgroundColor: '#fff',
                  '&:hover': {
                    transform: 'translateY(-12px)',
                    boxShadow: '0 30px 60px rgba(30, 136, 229, 0.1)',
                    borderColor: '#1E88E5',
                    '& .icon-box': {
                      backgroundColor: 'rgba(30, 136, 229, 0.1)',
                      color: '#1E88E5',
                      transform: 'scale(1.1) rotate(-5deg)',
                    }
                  }
                }}
              >
                <Box className="icon-box" sx={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '24px',
                  backgroundColor: '#F1F5F9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                  transition: 'all 0.4s ease',
                  color: '#64748B',
                }}>
                  <i className="iconfont icon-friends" style={{ fontSize: '2.5rem' }}></i>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, fontFamily: "'Poppins', sans-serif" }}>
                  {localization.strings.sendExistPassword}
                </Typography>
                <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                  {localization.strings.sendExistPasswordDesc}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                onClick={() => {
                  setStep(0)
                  setContentInfo({})
                  setDialogOpen(true)
                  setMode('sendNewPassword')
                  setDialogTitle(localization.strings.sendNewPassword)
                }}
                sx={{
                  p: 5,
                  height: '100%',
                  borderRadius: '32px',
                  border: '1px solid rgba(0,0,0,0.04)',
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  backgroundColor: '#fff',
                  '&:hover': {
                    transform: 'translateY(-12px)',
                    boxShadow: '0 30px 60px rgba(76, 175, 80, 0.1)',
                    borderColor: '#4CAF50',
                    '& .icon-box': {
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      color: '#4CAF50',
                      transform: 'scale(1.1) rotate(5deg)',
                    }
                  }
                }}
              >
                <Box className="icon-box" sx={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '24px',
                  backgroundColor: '#F1F5F9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                  transition: 'all 0.4s ease',
                  color: '#64748B',
                }}>
                  <i className="iconfont icon-add" style={{ fontSize: '2.5rem' }}></i>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, fontFamily: "'Poppins', sans-serif" }}>
                  {localization.strings.sendNewPassword}
                </Typography>
                <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                  {localization.strings.sendNewPasswordDesc}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      { /* Crypto Message Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => { if (step !== 2) setDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '24px', p: 1 }
        }}
      >
        <DialogTitle sx={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 800,
          textAlign: 'center',
          pt: 3, pb: 1
        }}>
          {dialogTitle}
        </DialogTitle>
        <DialogContent sx={{ overflowY: 'visible', px: 4, pb: 4 }}>
          {step < 3 && (
            <Stepper activeStep={step} sx={{ mb: 4, mt: 1 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          <Box sx={{ mt: 2 }}>
            {step === 0 && mode === 'sendExistPassword' && (
              <Fade in={true}>
                <Box>
                  <FormInput
                    label={localization.strings.keyword}
                    placeholder={localization.strings.keywordHint}
                    size="small"
                    onChange={(event) => setKeyword(event.target.value)}
                    style={{ mb: 2 }}
                  />
                  <Box sx={{
                    mt: 2,
                    maxHeight: '40vh',
                    overflowY: 'auto',
                    pr: 1,
                    '&::-webkit-scrollbar': { width: '4px' },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '10px' }
                  }}>
                    <Stack spacing={1}>
                      {contentList.filter((item) => {
                        if (keyword === '') return true
                        return item.title.toLowerCase().indexOf(keyword.toLowerCase()) >= 0
                      }).map((item) => (
                        <ContentItem
                          key={item.id}
                          contentType={item.type}
                          text={item.title}
                          onClick={() => {
                            setContentInfo({
                              title: item.title,
                              content: item.content,
                              type: item.type,
                              color: item.color,
                              account: item.account,
                              extra: item.extra,
                            })
                            setStep(1)
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </Fade>
            )}

            {step === 0 && mode === 'sendNewPassword' && (
              <Fade in={true}>
                <Box>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>{localization.strings.contentType}</InputLabel>
                    <Select
                      value={contentType}
                      label={localization.strings.contentType}
                      onChange={(event) => setContentType(parseInt(event.target.value))}
                      sx={{ borderRadius: '12px' }}
                    >
                      <MenuItem value={ContentUtils.getPasswordType()}>{localization.strings.password}</MenuItem>
                      <MenuItem value={ContentUtils.getTextType()}>{localization.strings.text}</MenuItem>
                      <MenuItem value={ContentUtils.getTotpType()}>{localization.strings.totp}</MenuItem>
                      <MenuItem value={ContentUtils.getBlockChainType()}>{localization.strings.blockchain}</MenuItem>
                    </Select>
                  </FormControl>

                  <Box sx={{ mb: 3 }}>
                    {contentType === ContentUtils.getPasswordType() && (<PasswordForm ref={passwordFormRef} contentInfo={contentInfo} />)}
                    {contentType === ContentUtils.getTextType() && (<TextForm ref={textFormRef} contentInfo={contentInfo} />)}
                    {contentType === ContentUtils.getTotpType() && (<TotpForm ref={totpFormRef} contentInfo={contentInfo} />)}
                    {contentType === ContentUtils.getBlockChainType() && (<BlockchainForm ref={blockchainFormRef} contentInfo={contentInfo} />)}
                  </Box>

                  <Stack direction="row" spacing={2}>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => setDialogOpen(false)}
                      sx={{ borderRadius: '12px', fontWeight: 700, color: '#64748B' }}
                    >
                      {localization.strings.cancel}
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => {
                        let contentData = {}
                        const checkRef = {
                          [ContentUtils.getPasswordType()]: passwordFormRef,
                          [ContentUtils.getTextType()]: textFormRef,
                          [ContentUtils.getTotpType()]: totpFormRef,
                          [ContentUtils.getBlockChainType()]: blockchainFormRef,
                        }[contentType];

                        if (checkRef?.current?.checkForm()) return;
                        contentData = checkRef?.current?.getContentData();

                        contentData.tags = []
                        setContentInfo(contentData)
                        setStep(1)
                      }}
                      sx={{ borderRadius: '12px', fontWeight: 700, py: 1.5 }}
                    >
                      {localization.strings.goNext}
                    </Button>
                  </Stack>
                </Box>
              </Fade>
            )}

            {step === 1 && (
              <Fade in={true}>
                <Box>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>{localization.strings.validityPeriod}</InputLabel>
                    <Select
                      value={validityPeriod}
                      label={localization.strings.validityPeriod}
                      onChange={(event) => setValidityPeriod(parseInt(event.target.value))}
                      sx={{ borderRadius: '12px' }}
                    >
                      {ValidityPeriods.map((item) => (
                        <MenuItem key={item.value} value={item.value}>{item.desc}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormInput
                    type="password"
                    label={localization.strings.tempPassword}
                    placeholder={localization.strings.inputTempPassHint}
                    onChange={(event) => {
                      setTempPassword(event.target.value)
                      setTempPasswordErr('')
                    }}
                    error={tempPasswordErr}
                    style={{ mb: 3, marginBottom: '20px' }}
                  />

                  <FormInput
                    label={`${localization.strings.messageHintWord} (${localization.strings.optional})`}
                    placeholder={localization.strings.inputMessageHintWordHint}
                    onChange={(event) => setHintWord(event.target.value)}
                    style={{ mb: 4, marginBottom: '20px' }}
                  />

                  <Stack direction="row" spacing={2}>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => setStep(0)}
                      sx={{ borderRadius: '12px', fontWeight: 700, color: '#64748B' }}
                    >
                      {localization.strings.goBack}
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={doPack}
                      sx={{ borderRadius: '12px', fontWeight: 700, py: 1.5 }}
                    >
                      {localization.strings.pack}
                    </Button>
                  </Stack>
                </Box>
              </Fade>
            )}

            {step === 2 && (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress size="4rem" thickness={5} sx={{ color: '#1E88E5', mb: 3 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                  {localization.strings.packing}
                </Typography>
                <Typography variant="body2" sx={{ color: '#94A3B8', mt: 1 }}>
                  {localization.strings.packingDesc}
                </Typography>
              </Box>
            )}

            {step === 3 && (
              <Zoom in={true}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" sx={{ color: '#1E293B', fontWeight: 600, mb: 3 }}>
                    {localization.strings.packResult}
                  </Typography>

                  <Stack alignItems="center" spacing={4}>
                    <Tooltip title={localization.strings.clickToCopyLink}>
                      <Box
                        onClick={() => {
                          copy(link)
                          toast.success(localization.strings.copySuccess)
                        }}
                        sx={{
                          p: 3,
                          borderRadius: '24px',
                          backgroundColor: '#F0F9FF',
                          border: '2px dashed #1E88E5',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          '&:hover': {
                            backgroundColor: '#E0F2FE',
                            transform: 'scale(1.02)'
                          }
                        }}
                      >
                        <i className="iconfont icon-link" style={{ fontSize: '1.8rem', color: '#1E88E5' }}></i>
                        <Typography variant="body2" sx={{
                          color: '#1E88E5',
                          fontWeight: 700,
                          maxWidth: '260px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {link}
                        </Typography>
                      </Box>
                    </Tooltip>

                    <Box sx={{
                      p: 2,
                      backgroundColor: '#fff',
                      borderRadius: '24px',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                      border: '1px solid rgba(0,0,0,0.04)'
                    }}>
                      <QRCodeSVG value={link} size={180} />
                    </Box>

                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setDialogOpen(false)}
                      sx={{ borderRadius: '12px', fontWeight: 700 }}
                    >
                      {localization.strings.done}
                    </Button>
                  </Stack>
                </Box>
              </Zoom>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default MainMessage;